"use client";
import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import { FileText, Send, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { GlassCard } from "@/src/components/GlassCard";
import { submitQuoteRequest } from "@/src/actions/quotes";
import { getQuoteFormOptions } from "@/src/actions/quoteOptions";
import type { QuoteFormOptions } from "@/src/types/quote-options";
import { getSettingsData } from "@/src/actions/settings";
import { Button } from "@/src/components/ui/button";

export const QuotePage: React.FC = () => {
  const { t, i18n } = useTranslation("common");
  const [options, setOptions] = useState<QuoteFormOptions | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    packagingType: "",
    dimensions: "",
    quantity: "",
    material: "",
    customPrinting: "no",
    timeline: "",
    budget: "",
    additionalInfo: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quoteNumber, setQuoteNumber] = useState<string | null>(null);

  useEffect(() => {
    getQuoteFormOptions(i18n.language).then(setOptions).catch(console.error);
  }, [i18n.language]);

  useEffect(() => {
    getSettingsData()
      .then((data) => {
        setFormData((prev) => ({
          ...prev,
          name:
            [data.firstName, data.lastName].filter(Boolean).join(" ") ||
            prev.name,
          email: data.email || prev.email,
          company: data.companyName || prev.company,
          phone: data.phone || prev.phone,
        }));
      })
      .catch(() => {
        // Not authenticated — leave defaults
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const result = await submitQuoteRequest({
      packaging_type: formData.packagingType,
      quantity: parseInt(formData.quantity, 10),
      timeline: formData.timeline,
      custom_printing: formData.customPrinting === "yes",
      dimensions: formData.dimensions || undefined,
      material: formData.material || undefined,
      budget_range: formData.budget || undefined,
      additional_info: formData.additionalInfo || undefined,
    });

    setLoading(false);

    if ("error" in result) {
      setError(result.error);
    } else {
      setQuoteNumber(result.quoteNumber);
      setSubmitted(true);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl w-full"
        >
          <GlassCard className="p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-4xl font-bold text-walnut-900 dark:text-white mb-4">
              {t("marketingPages.quote.successTitle")}
            </h2>
            {quoteNumber && (
              <p className="text-sm font-mono text-walnut-600 dark:text-walnut-400 mb-2">
                {t("marketingPages.quote.reference", { quoteNumber })}
              </p>
            )}
            <p className="text-lg text-walnut-700 dark:text-walnut-300 mb-8">
              {t("marketingPages.quote.successMessage")}
            </p>
            <div className="space-y-4 text-left max-w-md mx-auto mb-8">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-walnut-600 dark:text-walnut-400 flex-shrink-0 mt-0.5" />
                <span className="text-walnut-700 dark:text-walnut-300">
                  {t("marketingPages.quote.successStepEmail")}
                </span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-walnut-600 dark:text-walnut-400 flex-shrink-0 mt-0.5" />
                <span className="text-walnut-700 dark:text-walnut-300">
                  {t("marketingPages.quote.successStepContact")}
                </span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-walnut-600 dark:text-walnut-400 flex-shrink-0 mt-0.5" />
                <span className="text-walnut-700 dark:text-walnut-300">
                  {t("marketingPages.quote.successStepQuote")}
                </span>
              </div>
            </div>
            <Button
              variant="gradient"
              size="lg"
              onClick={() => setSubmitted(false)}
            >
              {t("marketingPages.quote.submitAnother")}
            </Button>
          </GlassCard>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-24">
        {/* Glassmorphism Background with Gradient Orbs */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          {/* Animated gradient orbs */}
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-walnut-500/30 dark:bg-walnut-600/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-beige-400/30 dark:bg-beige-700/15 rounded-full blur-[80px] animate-pulse" />
          <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] bg-grey-300/20 dark:bg-grey-700/10 rounded-full blur-[90px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block mb-6">
              <div className="backdrop-blur-xl bg-walnut-500/10 dark:bg-walnut-500/20 px-6 py-2 rounded-full border border-walnut-500/20 dark:border-walnut-400/30">
                <span className="text-walnut-700 dark:text-walnut-300 font-medium">
                  <FileText className="w-4 h-4 inline-block mr-2" />
                  {t("quotePage.hero.badge")}
                </span>
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-walnut-900 dark:text-white mb-6">
              {t("quotePage.hero.title")}
            </h1>
            <p className="text-xl text-walnut-700 dark:text-walnut-300 max-w-3xl mx-auto">
              {t("quotePage.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <GlassCard className="p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <FileText className="w-8 h-8 text-walnut-700 dark:text-walnut-300" />
              <h2 className="text-3xl font-bold text-walnut-900 dark:text-white">
                {t("marketingPages.quote.formTitle")}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <div>
                <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-4">
                  {t("marketingPages.quote.contactInfo")}
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-walnut-900 dark:text-white mb-2"
                    >
                      {t("marketingPages.quote.fullName")}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white focus:outline-none focus:border-walnut-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-walnut-900 dark:text-white mb-2"
                    >
                      {t("marketingPages.quote.email")}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white focus:outline-none focus:border-walnut-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-walnut-900 dark:text-white mb-2"
                    >
                      {t("marketingPages.quote.company")}
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white focus:outline-none focus:border-walnut-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-walnut-900 dark:text-white mb-2"
                    >
                      {t("marketingPages.quote.phone")}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white focus:outline-none focus:border-walnut-500"
                    />
                  </div>
                </div>
              </div>

              {/* Packaging Details */}
              <div>
                <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-4">
                  {t("marketingPages.quote.packagingDetails")}
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="packagingType"
                      className="block text-walnut-900 dark:text-white mb-2"
                    >
                      {t("marketingPages.quote.packagingType")}
                    </label>
                    <select
                      id="packagingType"
                      name="packagingType"
                      value={formData.packagingType}
                      onChange={handleChange}
                      required
                      disabled={!options}
                      className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white focus:outline-none focus:border-walnut-500 disabled:opacity-50"
                    >
                      <option value="">{t("marketingPages.quote.selectType")}</option>
                      {options?.packagingTypes.map((o) => (
                        <option key={o.code} value={o.code}>{o.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="dimensions"
                      className="block text-walnut-900 dark:text-white mb-2"
                    >
                      {t("marketingPages.quote.dimensions")}
                    </label>
                    <input
                      type="text"
                      id="dimensions"
                      name="dimensions"
                      value={formData.dimensions}
                      onChange={handleChange}
                      placeholder={t("marketingPages.quote.dimensionsPlaceholder")}
                      className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white focus:outline-none focus:border-walnut-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="quantity"
                      className="block text-walnut-900 dark:text-white mb-2"
                    >
                      {t("marketingPages.quote.quantity")}
                    </label>
                    <input
                      type="number"
                      id="quantity"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      required
                      placeholder={t("marketingPages.quote.quantityPlaceholder")}
                      className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white focus:outline-none focus:border-walnut-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="material"
                      className="block text-walnut-900 dark:text-white mb-2"
                    >
                      {t("marketingPages.quote.materialPreference")}
                    </label>
                    <select
                      id="material"
                      name="material"
                      value={formData.material}
                      onChange={handleChange}
                      disabled={!options}
                      className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white focus:outline-none focus:border-walnut-500 disabled:opacity-50"
                    >
                      <option value="">{t("marketingPages.quote.selectMaterial")}</option>
                      {options?.materials.map((o) => (
                        <option key={o.code} value={o.code}>{o.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Requirements */}
              <div>
                <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-4">
                  {t("marketingPages.quote.additionalRequirements")}
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="customPrinting"
                      className="block text-walnut-900 dark:text-white mb-2"
                    >
                      {t("marketingPages.quote.customPrinting")}
                    </label>
                    <select
                      id="customPrinting"
                      name="customPrinting"
                      value={formData.customPrinting}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white focus:outline-none focus:border-walnut-500"
                    >
                      <option value="no">{t("marketingPages.quote.no")}</option>
                      <option value="yes">{t("marketingPages.quote.yes")}</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="timeline"
                      className="block text-walnut-900 dark:text-white mb-2"
                    >
                      {t("marketingPages.quote.timeline")}
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      required
                      disabled={!options}
                      className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white focus:outline-none focus:border-walnut-500 disabled:opacity-50"
                    >
                      <option value="">{t("marketingPages.quote.selectTimeline")}</option>
                      {options?.timelines.map((o) => (
                        <option key={o.code} value={o.code}>{o.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="budget"
                      className="block text-walnut-900 dark:text-white mb-2"
                    >
                      {t("marketingPages.quote.budgetRange")}
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      disabled={!options}
                      className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white focus:outline-none focus:border-walnut-500 disabled:opacity-50"
                    >
                      <option value="">{t("marketingPages.quote.selectBudget")}</option>
                      {options?.budgetRanges.map((o) => (
                        <option key={o.code} value={o.code}>{o.title}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <label
                  htmlFor="additionalInfo"
                  className="block text-walnut-900 dark:text-white mb-2"
                >
                  {t("marketingPages.quote.additionalInfo")}
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white focus:outline-none focus:border-walnut-500 resize-none"
                  placeholder={t("marketingPages.quote.additionalInfoPlaceholder")}
                />
              </div>

              {error && (
                <p className="text-red-600 dark:text-red-400 text-sm">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                variant="gradient"
                size="lg"
                disabled={loading}
                className="w-full md:w-auto"
              >
                {loading
                  ? t("marketingPages.quote.submitting")
                  : t("marketingPages.quote.submit")}
                <Send className="w-5 h-5" />
              </Button>
            </form>
          </GlassCard>
        </div>
      </section>
    </div>
  );
};
